import { NamedExoticComponent } from 'react';

import Envelope from '@ast/magma/components/icon/icons/Envelope';
import MapMarker from '@ast/magma/components/icon/icons/MapMarker';
import MobileDevice from '@ast/magma/components/icon/icons/MobileDevice';
import ShieldCheck from '@ast/magma/components/icon/icons/ShieldCheck';

import { Field } from '@app/common/configurable-wizards/types';

import { t } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

import { CHANGE_PASSWORD_PATH, PROFILE_SECURITY_PATH } from '../const';
import {
  SectionIDs,
  AddressFields,
  PhoneFields,
  EmailFields,
  ProfileDetailsFields,
  PasswordFields,
} from '../types';

import { userEventsLogsField } from '../fields/ProfileAndSecurity/Security/UserEventLogs/DisplayUserEventLogField';
import { TIME_ZONE_SECTION_ID } from '../fields/ProfileAndSecurity/UserProfile/UserProfileList';

export type Section = {
  id: string;
  title: string;
  subTitle: string | Map<string | number, string>;
  icon: NamedExoticComponent;
  serverFields: typeof AddressFields | typeof PhoneFields | typeof EmailFields | typeof ProfileDetailsFields;
};

export interface RenderSection extends Section {
  fields: Field[];
}

export type SectionGroup = {
  title: string;
  sections: Array<Section>;
};

export type RenderSectionGroup = {
  title: string;
  sections: Array<RenderSection>;
};

export const contactsSectionGroups: Array<SectionGroup> = [
  {
    title: t(
      'settings-section.profile.title|Title of the profile settings group',
      'Profile Details',
    ),
    sections: [
      {
        id: PROFILE_SECURITY_PATH,
        serverFields: ProfileDetailsFields,
        title: t(
          'settings-section.profile.profile-and-security.title|Profile & security settings section title',
          'Profile & Security',
        ),
        subTitle: new Map([
          [
            CHANGE_PASSWORD_PATH,
            t(
              'settings-section.profile.profile-and-security.password.title|User password settings subsection title',
              'Edit Password',
            ),
          ],
          [
            userEventsLogsField.id,
            t(
              'settings-section.profile.profile-and-security.event-logs.title|Event logs subsection title',
              'Event Logs',
            ),
          ],
          [
            TIME_ZONE_SECTION_ID,
            t(
              'settings-section.profile.profile-and-security.time-zone.title|Time zone subsection title',
              'Time zone',
            ),
          ],
          [
            PasswordFields.Passkey,
            t(
              'settings-section.profile.profile-and-security.passkey.title|Passkey settings subsection title',
              'Passkeys',
            ),
          ],
        ]),
        icon: ShieldCheck,
      },
    ],
  },
  {
    title: t(
      'settings-section.contacts.title|Title of the contact settings group',
      'Contact Details',
    ),
    sections: [
      {
        id: SectionIDs.ADDRESS,
        serverFields: AddressFields,
        title: t(
          'settings-section.contacts.address.title|User contacts settings address section title',
          'Address',
        ),
        subTitle: t(
          'settings-section.contacts.address.subTitle|User contacts settings address subsection title',
          'Address details',
        ),
        icon: MapMarker,
      }, {
        id: SectionIDs.PHONE,
        serverFields: PhoneFields,
        title: t(
          'settings-section.contacts.phone.title|User contacts settings phone section title',
          'Phone',
        ),
        subTitle: t(
          'settings-section.contacts.phone.subTitle|User contacts settings phone subsection title',
          'Phone number details',
        ),
        icon: MobileDevice,
      }, {
        id: SectionIDs.EMAIL,
        serverFields: EmailFields,
        title: t(
          'settings-section.contacts.email.title|User contacts settings email section title',
          'Email',
        ),
        subTitle: t(
          'settings-section.contacts.email.subTitle|User contacts settings email subsection title',
          'Email address details',
        ),
        icon: Envelope,
      },
    ],
  },
];

export const getSettingsSections = (fields: Field[]): Array<RenderSectionGroup> => {
  const hasFields = fields?.length > 0;

  if (!hasFields) {
    return [];
  }

  const sections = contactsSectionGroups.map((sectionGroup) => ({
    ...sectionGroup,
    sections: sectionGroup.sections
      .map((section) => {
        const sectionFields = fields.filter(
          ({ id, isEnabled }) => id in section.serverFields && isEnabled,
        );

        return {
          ...section,
          fields: sectionFields,
        };
      })
      .filter((section) => section.fields && section.fields.length > 0),
  }));

  return sections;
};
